import { Box, Typography, Collapse, CircularProgress } from '@mui/material';
import { useState, useMemo } from 'react';
import { useFetchResponseQuery } from '@tymely/services';

import { TEMPLATE_METADATA_ID } from '../../constants';
import { FollowUpEventProps } from '../types';
import { FollowUpPromptButton, FollowUpPromptBox } from './FollowUpEvent.styles';

export const FollowUpEvent = ({ eventData, isExpanded, workflowId }: FollowUpEventProps) => {
    const [showScenarioPrompt, setShowScenarioPrompt] = useState(false);
    const decision = eventData.decision;

    const templateId = useMemo(() => {
        if (!decision?.action_executions) return undefined;
        const actionExecution = decision.action_executions.find(
            (ae) => ae.action_metadata_id === TEMPLATE_METADATA_ID || ae.action_metadata?.id === TEMPLATE_METADATA_ID,
        );
        return actionExecution?.additional_data?.args_template?.template_id;
    }, [decision?.action_executions]);

    const { data: templateData, isLoading: isTemplateLoading } = useFetchResponseQuery(templateId);

    if (!decision) return null;

    const { title, workflow_id, policy_set_id } = decision;

    const textElements = [
        {
            condition: title,
            label: 'Title',
            content: title,
        },
        { condition: workflow_id || workflowId, label: 'Workflow Id', content: workflow_id || workflowId },
        { condition: policy_set_id, label: 'Intend Id', content: policy_set_id },
    ];

    return (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 2, display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {textElements.map(
                    ({ condition, label, content }, index) =>
                        condition &&
                        content && (
                            <Typography
                                key={index}
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    whiteSpace: 'normal',
                                    fontFamily: 'inherit',
                                }}
                            >
                                {label && <>{label}: </>}
                                {typeof content === 'string' ? <b>{content}</b> : content}
                            </Typography>
                        ),
                )}
                {templateId && (
                    <Box>
                        <FollowUpPromptButton
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowScenarioPrompt(!showScenarioPrompt)}
                            onKeyDown={(e: React.KeyboardEvent) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    setShowScenarioPrompt(!showScenarioPrompt);
                                }
                            }}
                        >
                            Respond and resolve prompt
                        </FollowUpPromptButton>
                        <Collapse in={showScenarioPrompt}>
                            {isTemplateLoading ? (
                                <Box sx={{ mt: 1.25 }}>
                                    <CircularProgress size={20} />
                                </Box>
                            ) : (
                                <FollowUpPromptBox>
                                    <Typography variant="body2" color="text.secondary">
                                        {templateData?.response_txt}
                                    </Typography>
                                </FollowUpPromptBox>
                            )}
                        </Collapse>
                    </Box>
                )}
            </Box>
        </Collapse>
    );
};
